/**
 * Bounded, priority-ordered admission control for canvas node media.
 *
 * Why this exists (Workplace post 1596425285448102): every canvas node card
 * requests its asset at FULL resolution — `getThumbnailUrl(..., 0, ...)` emits
 * no `w=` param, and the API ignores `w` anyway (`assets.py`, "Resize width
 * (ignored, thumbnails pre-generated)"). ReactFlow mounts every in-viewport
 * node at once, so zooming out on a dense project hands the browser several
 * hundred full-size image requests in one frame. The browser's per-origin
 * connection limit then serializes them in DOM order, so the top rows paint
 * and everything else sits as an empty box until its turn — and each pan
 * remounts nodes and restarts the wave.
 *
 * We cannot make the images smaller from the frontend, so instead we control
 * HOW MANY start at once. This module hands out a bounded number of "slots";
 * a card may mount its `<img>` only while holding one, and releases it when
 * the browser reports load/error. Waiting cards are admitted nearest-to-
 * viewport-centre first, so the view fills outward from where the user is
 * looking instead of top-down.
 *
 * Note this is admission control, NOT preloading: the real `<img>` performs
 * the fetch. Because of that, releasing a slot EARLY is harmless — the slot is
 * only a permission token, and the `<img>` keeps loading either way. That is
 * what makes the watchdog below a safe backstop rather than a correctness risk.
 *
 * Deliberately NOT done here: hiding previews below a zoom threshold. That
 * regressed before (the retired `CanvasViewportPreviewLayer` caused low-zoom
 * preview disappearance) and is the symptom we are fixing, not the fix.
 */
import { createNadDebug } from '../../utils/debugLog'

const featureLog = createNadDebug('dw:canvas-image-loading')

/**
 * Concurrent full-size canvas image loads. Chrome allows ~6 connections per
 * origin over HTTP/1.1 and more over HTTP/2; 8 keeps the pipe busy without
 * letting the canvas monopolise it against API/websocket traffic.
 */
const MAX_CONCURRENT = 8

/**
 * Backstop for a slot whose holder never reports load/error.
 *
 * A leaked slot is far worse than the bug this module fixes: eight of them
 * exhaust the pool and every remaining card stays blank forever. Consumers are
 * expected to call `releaseCanvasImageSlot`, but a component can always render
 * a branch that has no `<img>` at all, so the module must not depend on their
 * good behaviour. Releasing early only lets another card start; it never
 * cancels the in-flight request.
 */
export const SLOT_WATCHDOG_MS = 15_000

type Waiter = {
  url: string
  /** Lower is admitted sooner. Distance in px from viewport centre. */
  priority: number
  admit: () => void
}

/**
 * URLs that have been admitted at least once this session.
 *
 * Used ONLY to prioritise: a URL the user has already seen is very likely a
 * browser-cache hit, so it goes to the front of the queue and its slot frees
 * almost immediately. It is deliberately NOT a slot bypass — browser cache
 * evicts and revalidates, so "seen before" is not proof of a cache hit, and
 * treating it as one let an unbounded number of re-entering cards fetch with
 * no reservation at all (the throttle silently weakened over a long session).
 */
const admitted = new Set<string>()
/** URLs holding a live slot — admitted but not yet load/error-reported. */
const holding = new Set<string>()
/** Watchdog timers, keyed by the URL whose slot they guard. */
const watchdogs = new Map<string, ReturnType<typeof setTimeout>>()

let waiters: Waiter[] = []

/**
 * Canvas media is "loading" while any card is queued or holding a slot.
 *
 * Consumed by Arrange: it snapshots measured node/media dimensions and rejects
 * its result as stale if they change during the planning request, so starting
 * it while images are still arriving fails with "Canvas changed while
 * arranging" even though the user edited nothing.
 */
let mediaLoading = false
const mediaLoadingListeners = new Set<() => void>()
/**
 * Unsettled admissions per url. `holding` is url-keyed (one slot per url), so
 * two cards showing the same asset share an entry and the first release would
 * otherwise report "settled" while the second card is still loading.
 */
const pendingLoads = new Map<string, number>()

function syncMediaLoading(): void {
  const next = waiters.length > 0 || pendingLoads.size > 0
  if (next === mediaLoading) return
  mediaLoading = next
  featureLog({ event: 'canvas-media-loading', loading: next, queued: waiters.length, holding: holding.size, pending: pendingLoads.size })
  for (const listener of mediaLoadingListeners) listener()
}

export function isCanvasMediaLoading(): boolean {
  return mediaLoading
}

export function subscribeCanvasMediaLoading(listener: () => void): () => void {
  mediaLoadingListeners.add(listener)
  return () => {
    mediaLoadingListeners.delete(listener)
  }
}

function reserve(url: string): void {
  admitted.add(url)
  holding.add(url)
  pendingLoads.set(url, (pendingLoads.get(url) ?? 0) + 1)
  syncMediaLoading()
  const existing = watchdogs.get(url)
  if (existing) clearTimeout(existing)
  watchdogs.set(
    url,
    setTimeout(() => {
      if ((pendingLoads.get(url) ?? 0) === 0) return
      featureLog({ event: 'canvas-image-slot-watchdog', url, holding: holding.size })
      // Shared urls have one timer; expiry settles every remaining admission.
      pendingLoads.delete(url)
      releaseCanvasImageSlot(url)
    }, SLOT_WATCHDOG_MS),
  )
}

/**
 * True while the user is panning or zooming the VIEWPORT.
 *
 * Node dragging is deliberately not wired: it runs through a separate
 * interaction path (`beginNodeDragInteraction`), and moving a node does not
 * scroll new nodes into view, so it does not produce the mount burst this
 * gate exists to spread out.
 *
 * Starting an image load mid-gesture is the worst possible moment for it: with
 * `onlyRenderVisibleElements` on, panning MOUNTS nodes as they scroll into view,
 * and each new `<img>` costs a main-thread decode — at full resolution, because
 * the API ignores `?w=`. A decode landing between two pan frames is exactly the
 * stutter users report as "it hitches when a node with an image appears".
 *
 * So while a gesture is in flight we stop ADMITTING new urls and let them queue;
 * `endCanvasGesture()` drains the queue once the canvas settles. Because the
 * queue is already ordered by distance from the viewport centre, the post-gesture
 * drain also happens to load what the user is looking at first.
 *
 * This deliberately does NOT touch anything already admitted or already on
 * screen. An earlier generation of canvas experiments hid live previews during
 * movement and produced disappearing images and video black-flashes; this only
 * delays the FIRST mount of media that is not visible yet, so nothing that was
 * on screen can vanish.
 */
let gestureActive = false

/**
 * How long after a gesture ends before queued media is allowed to load.
 *
 * Lives here rather than on `endViewportInteraction` deliberately: that timer
 * also drives the `will-change` interaction classes, which exist to keep text
 * crisp at idle zoom, so lengthening it would blur text for the same window.
 * Media settling and paint-hinting are separate concerns and get separate dials.
 *
 * Note this window is a REFINEMENT, not the main mechanism. Most of the benefit
 * comes from the gate being shut *during* the gesture, so no decode lands
 * between two pan frames; the settle only covers the tail, where the browser is
 * still catching up on layout and paint from the gesture just released. Its
 * value therefore has steep diminishing returns.
 *
 * Held at 1s rather than longer because the window has a real cost: while it is
 * open, `gestureActive` is still true, so media arriving from a YDoc update —
 * a finished generation, a variant switch, an upstream reference change — also
 * waits. The card keeps showing its previous image rather than blanking, but
 * "pan, then immediately click a variant" is a visible delay. The proper fix is
 * to gate FIRST MOUNTS only (a replacement url is user- or generation-driven,
 * never pan-driven), after which this number stops being a trade-off at all.
 * Until then, keep it short enough to read as loading rather than as broken.
 */
export const CANVAS_MEDIA_SETTLE_MS = 1_000

/**
 * Fail-safe: open the gate this long after the last sign of gesture ACTIVITY.
 *
 * Deliberately an inactivity watchdog rather than a cap measured from the start
 * of the gesture. Those are different questions and a start-relative timer
 * conflates them: "the user is still panning" (defer — that is the whole point)
 * versus "the end event never arrived" (recover, or the canvas stays blank
 * forever). Timing from the last observed movement separates them, so a long
 * continuous pan defers for as long as it lasts, while a lost end still
 * recovers shortly after movement actually stops.
 *
 * Larger than the settle so it cannot fire while a normal settle is pending.
 */
export const GESTURE_IDLE_TIMEOUT_MS = 2_000

/**
 * A url seen earlier this session keeps loading DURING a gesture.
 *
 * Without this, a long settle punishes the most common navigation there is —
 * panning back to where you just were. Those cards unmount on the way out and
 * re-acquire on the way in, so they would blank for the full settle window
 * despite almost certainly being warm in the browser cache.
 *
 * This is NOT the slot bypass the `admitted` docstring warns against: these
 * urls still take a real slot and stay inside MAX_CONCURRENT. They are exempt
 * from the *gesture* gate only, so the bound that comment protects is intact.
 */
const PREVIOUSLY_SEEN_LOADS_DURING_GESTURE = true

let gestureFailsafe: ReturnType<typeof setTimeout> | null = null
let settleTimer: ReturnType<typeof setTimeout> | null = null
/** Timestamp of the last observed gesture movement; drives the idle watchdog. */
let lastGestureActivityAt = 0

function clearGestureFailsafe(): void {
  if (gestureFailsafe === null) return
  clearTimeout(gestureFailsafe)
  gestureFailsafe = null
}

function clearSettleTimer(): void {
  if (settleTimer === null) return
  clearTimeout(settleTimer)
  settleTimer = null
}

/**
 * Mark a canvas gesture as in flight. Idempotent — repeated calls (ReactFlow
 * fires `onMoveStart` per gesture, and a drag can overlap a pan) just refresh
 * the fail-safe rather than nesting a counter, which would strand the gate open
 * if starts and ends ever failed to balance.
 */
function armIdleWatchdog(delayMs: number): void {
  clearGestureFailsafe()
  gestureFailsafe = setTimeout(() => {
    gestureFailsafe = null
    // Re-check against the last observed movement instead of opening blind: a
    // gesture that is still producing move events has simply outlived one
    // watchdog period, which is not the failure this guards.
    const idleFor = Date.now() - lastGestureActivityAt
    if (idleFor < GESTURE_IDLE_TIMEOUT_MS) {
      armIdleWatchdog(GESTURE_IDLE_TIMEOUT_MS - idleFor)
      return
    }
    featureLog({ event: 'canvas-image-gesture-failsafe', queued: waiters.length, idleFor })
    openGate('failsafe')
  }, delayMs)
}

export function beginCanvasGesture(): void {
  // A new gesture cancels a pending settle: the user kept moving, so the queue
  // must stay held rather than draining into the middle of the next pan.
  clearSettleTimer()
  gestureActive = true
  lastGestureActivityAt = Date.now()
  armIdleWatchdog(GESTURE_IDLE_TIMEOUT_MS)
}

/**
 * Report that the gesture is still alive (wire to a continuous move handler).
 *
 * Cheap by construction — a single timestamp write, no timer churn, so it is
 * safe to call at frame rate. The watchdog reads it when it fires rather than
 * being re-armed on every frame.
 */
export function touchCanvasGesture(): void {
  lastGestureActivityAt = Date.now()
}

function openGate(reason: string): void {
  gestureActive = false
  clearGestureFailsafe()
  clearSettleTimer()
  if (waiters.length > 0) {
    featureLog({
      event: 'canvas-image-gesture-drain',
      reason,
      queued: waiters.length,
      holding: holding.size,
    })
  }
  pump()
}

/**
 * Gesture finished — drain after CANVAS_MEDIA_SETTLE_MS.
 *
 * The delay is the point: releasing the mouse is exactly when the browser is
 * still catching up on layout and paint from the gesture, so draining on the
 * release edge puts the decode burst in the worst place. Waiting lets the
 * canvas go quiet first.
 */
export function endCanvasGesture(): void {
  if (!gestureActive) return
  clearGestureFailsafe()
  clearSettleTimer()
  settleTimer = setTimeout(() => {
    settleTimer = null
    openGate('settled')
  }, CANVAS_MEDIA_SETTLE_MS)
}

/** Test/diagnostic seam. */
export function isCanvasGestureActive(): boolean {
  return gestureActive
}

function pump(): void {
  if (waiters.length === 0 || holding.size >= MAX_CONCURRENT) return
  // Sorting on pump (not insert) keeps enqueue O(1) during the burst where
  // hundreds of nodes mount in a single frame.
  waiters.sort((a, b) => a.priority - b.priority)

  if (gestureActive) {
    // Mid-gesture: serve ONLY previously-seen urls (likely cache hits, so no
    // decode burst), still bounded by MAX_CONCURRENT. Everything else stays
    // queued — deferred, never dropped — and drains once the canvas settles.
    if (!PREVIOUSLY_SEEN_LOADS_DURING_GESTURE) return
    for (let i = 0; i < waiters.length && holding.size < MAX_CONCURRENT; ) {
      const waiter = waiters[i]
      if (admitted.has(waiter.url)) {
        waiters.splice(i, 1)
        reserve(waiter.url)
        waiter.admit()
      } else {
        i++
      }
    }
    return
  }

  while (holding.size < MAX_CONCURRENT && waiters.length > 0) {
    const next = waiters.shift()
    if (!next) break
    reserve(next.url)
    next.admit()
  }
}

/**
 * True when `url` has been shown before, so it is likely a cache hit and
 * should jump the queue. Not a licence to skip reservation.
 */
export function isCanvasImageAdmitted(url: string): boolean {
  return admitted.has(url)
}

/**
 * Take a slot immediately if one is free, RESERVING it as a side effect.
 *
 * Callers must not substitute a read-only capacity check here. An earlier
 * revision exposed a pure `canAdmit()` predicate consulted during render, so
 * every card in a mount burst observed `holding === 0` in the same pass, all
 * admitted themselves, and nothing was ever throttled — the module silently
 * no-opped on the one path it exists for. Reserving is what makes the bound
 * real.
 *
 * @returns true when the caller may render immediately.
 */
export function tryAdmitCanvasImageNow(url: string): boolean {
  // Mid-gesture the fast path is gated too — otherwise a node scrolling into
  // view during a pan would still fire its decode immediately and the deferral
  // would only apply to the queued overflow. A previously-seen url is exempt
  // (likely a cache hit, so no decode burst) but still takes a real slot, and
  // skips the FIFO check below so it is not stuck behind deferred newcomers.
  if (gestureActive) {
    if (!PREVIOUSLY_SEEN_LOADS_DURING_GESTURE || !admitted.has(url)) return false
    if (holding.size >= MAX_CONCURRENT) return false
    reserve(url)
    return true
  }
  // Respect FIFO fairness: if anything is already waiting, queue behind it
  // rather than jumping ahead just because a slot happens to be free.
  if (waiters.length > 0 || holding.size >= MAX_CONCURRENT) return false
  reserve(url)
  return true
}

/**
 * Ask for permission to mount an `<img>` for `url`.
 *
 * @param priority Distance (px) from the viewport centre. Lower admits sooner.
 *                 Previously-seen URLs are promoted to the front of the queue.
 * @returns Resolves once the caller may render the image.
 */
export function acquireCanvasImageSlot(url: string, priority: number): Promise<void> {
  const effectivePriority = admitted.has(url) ? -1 : priority
  return new Promise<void>((resolve) => {
    waiters.push({ url, priority: effectivePriority, admit: resolve })
    syncMediaLoading()
    if (waiters.length === 1 || waiters.length % 50 === 0) {
      featureLog({
        event: 'canvas-image-queued',
        queued: waiters.length,
        holding: holding.size,
        admitted: admitted.size,
        priority: Math.round(effectivePriority),
      })
    }
    pump()
  })
}

/**
 * Report that `url` finished loading (or failed), or that its holder is gone.
 * Frees the slot for the next waiting card. Safe to call for a URL that is not
 * holding a slot, and safe to call twice.
 */
export function releaseCanvasImageSlot(url: string): void {
  const pending = pendingLoads.get(url) ?? 0
  // Keep the shared timer while another card is still loading this url.
  if (pending <= 1) {
    const timer = watchdogs.get(url)
    if (timer) {
      clearTimeout(timer)
      watchdogs.delete(url)
    }
  }
  if (pending > 1) pendingLoads.set(url, pending - 1)
  else pendingLoads.delete(url)
  if (holding.delete(url)) pump()
  syncMediaLoading()
}

/** Test seam: reset all module state between cases. */
export function __resetCanvasImageLoaderForTests(): void {
  for (const timer of watchdogs.values()) clearTimeout(timer)
  watchdogs.clear()
  admitted.clear()
  holding.clear()
  waiters = []
  gestureActive = false
  clearGestureFailsafe()
  clearSettleTimer()
  lastGestureActivityAt = 0
  mediaLoading = false
  mediaLoadingListeners.clear()
  pendingLoads.clear()
}

/** Test/diagnostic seam. */
export function __canvasImageLoaderStats(): {
  queued: number
  holding: number
  admitted: number
} {
  return { queued: waiters.length, holding: holding.size, admitted: admitted.size }
}
